import { Result } from "../../../shared/api/services/result";
import { AuthApiService } from "../../auth/api/AuthApiService";
import { uploadErrorMessageFromText } from "../../../shared/utils/uploadErrors";

/**
 * API Service for Onboarding existing clinics workflow
 * Endpoint prefix: /clinic/doctor/onboarding/
 */
export class OnboardingApiService {
  private baseURL: string;
  private authApi = AuthApiService.getInstance();

  constructor() {
    this.baseURL = process.env.REACT_APP_API_BASE_URL || "http://localhost:8000";
  }

  /**
   * Create an onboarding request (register existing clinic)
   * POST /clinic/doctor/onboarding/
   */
  async submitClinicInfo(clinicInfo: any): Promise<Result<any>> {
    try {
      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/clinic/doctor/onboarding/`,
        {
          method: "POST",
          body: JSON.stringify(clinicInfo),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(
          error.error_message || error.detail || "Failed to submit clinic info for onboarding"
        );
      }

      const result = await response.json();
      return Result.success(result.data || result);
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to submit clinic info for onboarding"
      );
    }
  }

  /**
   * Update an existing onboarding request
   * PUT /clinic/doctor/onboarding/{id}/
   */
  async updateClinicInfo(requestId: string, clinicInfo: any): Promise<Result<any>> {
    try {
      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/clinic/doctor/onboarding/${requestId}`,
        {
          method: "PUT",
          body: JSON.stringify(clinicInfo),
        }
      );

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(
          error.error_message || error.detail || "Failed to update clinic info for onboarding"
        );
      }

      const result = await response.json();
      return Result.success(result.data || result);
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to update clinic info for onboarding"
      );
    }
  }

  /**
   * Get onboarding requests for the current doctor
   * GET /clinic/doctor/onboarding/
   */
  async getOnboardingRequests(): Promise<Result<any[]>> {
    try {
      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/clinic/doctor/onboarding/`
      );

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(
          error.error_message || error.detail || "Failed to get onboarding requests"
        );
      }

      const result = await response.json();
      return Result.success(result.data || []);
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to get onboarding requests"
      );
    }
  }

  /**
   * Get a single onboarding request with full attachment data
   * GET /clinic/doctor/onboarding/{id}/with-attachments
   */
  async getOnboardingWithAttachments(requestId: string): Promise<Result<any>> {
    try {
      const response = await this.authApi.fetchWithAuth(
        `${this.baseURL}/clinic/doctor/onboarding/${requestId}/with-attachments`
      );

      if (!response.ok) {
        const error = await response.json();
        return Result.failure(
          error.error_message || error.detail || "Failed to get onboarding details"
        );
      }

      const result = await response.json();
      return Result.success(result.data || result);
    } catch (error) {
      return Result.failure(
        error instanceof Error ? error.message : "Failed to get onboarding details"
      );
    }
  }

  /**
   * Upload attachment for an onboarding request
   * POST /clinic/doctor/onboarding/{id}/attachments/upload
   */
  async uploadAttachment(
    requestId: string,
    file: File,
    documentType: string,
    onProgress?: (progress: number) => void
  ): Promise<Result<any>> {
    const accessToken = this.authApi.getAccessToken();
    if (!accessToken) {
      return Result.failure("No authentication token available");
    }

    try {
      const url = `${this.baseURL}/clinic/doctor/onboarding/${requestId}/attachments/upload`;

      return new Promise((resolve) => {
        const xhr = new XMLHttpRequest();
        const formData = new FormData();

        formData.append("file", file, file.name);
        formData.append("document_type", documentType);

        if (onProgress) {
          xhr.upload.addEventListener("progress", (e) => {
            if (e.lengthComputable) {
              const percentComplete = Math.round((e.loaded / e.total) * 100);
              onProgress(percentComplete);
            }
          });
        }

        xhr.onload = async () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const responseData = JSON.parse(xhr.responseText);
              console.log(`✅ [Onboarding] Uploaded ${documentType} successfully`);
              resolve(Result.success(responseData.data || responseData));
            } catch {
              resolve(Result.success({ message: "Upload successful" }));
            }
          } else if (xhr.status === 401 || xhr.status === 444) {
            const refreshResult = await this.authApi.refreshAccessToken();
            if (refreshResult.isSuccess) {
              resolve(this.uploadAttachment(requestId, file, documentType, onProgress));
            } else {
              resolve(Result.failure("Authentication failed"));
            }
          } else {
            console.error(`❌ [Onboarding] Upload failed with status ${xhr.status}:`, xhr.responseText);
            resolve(Result.failure(uploadErrorMessageFromText(xhr.status, xhr.responseText)));
          }
        };

        xhr.onerror = () => {
          console.error("❌ [Onboarding] Network error during upload");
          resolve(Result.failure("Network error during upload"));
        };

        xhr.ontimeout = () => {
          console.error("❌ [Onboarding] Upload timed out");
          resolve(Result.failure("Upload timed out"));
        };

        xhr.open("POST", url);
        xhr.setRequestHeader("Authorization", `Bearer ${accessToken}`);
        xhr.timeout = 60000;

        console.log(`📤 [Onboarding] Uploading ${documentType}: ${file.name} (${file.size} bytes)`);
        xhr.send(formData);
      });
    } catch (error) {
      console.error("❌ [Onboarding] Exception during upload:", error);
      return Result.failure(
        error instanceof Error ? error.message : "Failed to upload attachment"
      );
    }
  }
}
